// Page "seen" rule of docs/RUNBOOK-document-page.md §3.2: a page counts as
// seen once its visible height has been >= min(0.5 x page height,
// 0.9 x viewer height) for at least one second in total. Time only accrues
// while the page is rendered, the tab is visible and nothing covers the
// viewer; the viewer decides that and reports it through setQualifying()
// and tick(). No DOM access here, so the rule can be tested on its own.

export const SEEN_MS = 1000;

// a long gap between ticks (tab frozen, laptop asleep) is not reading time
const MAX_TICK_MS = 1000;

export const qualifies = (visibleHeight, pageHeight, rootHeight) =>
  pageHeight > 0 &&
  rootHeight > 0 &&
  visibleHeight >= Math.min(0.5 * pageHeight, 0.9 * rootHeight);

export default class SeenTracker {
  // restored: {pageNumber: firstSeenAt} from an earlier visit (optional)
  constructor(pageCount, restored = null) {
    this.pageCount = pageCount;
    this.pages = [];
    for (let page = 1; page <= pageCount; page++) {
      const firstSeenAt = restored?.[page] || null;
      this.pages.push({
        page,
        firstSeenAt,
        lastSeenAt: firstSeenAt,
        visibleMs: 0,
      });
    }
    this.qualifying = new Set();
    this.lastTick = null;
  }

  // page (1-based) currently meets the visibility rule, or stopped meeting it
  setQualifying(page, value, now) {
    if (page < 1 || page > this.pageCount) return [];
    const has = this.qualifying.has(page);
    if (has === !!value) return [];
    const seen = this.tick(now);
    if (value) this.qualifying.add(page);
    else this.qualifying.delete(page);
    return seen;
  }

  clearQualifying(now) {
    const seen = this.tick(now);
    this.qualifying.clear();
    return seen;
  }

  get active() {
    return this.qualifying.size > 0;
  }

  // accrues the time since the last tick to every qualifying page and
  // returns the page numbers that became seen now
  tick(now) {
    const last = this.lastTick;
    this.lastTick = now;
    if (last === null || !this.qualifying.size) return [];

    const delta = Math.min(Math.max(now - last, 0), MAX_TICK_MS);
    const newlySeen = [];
    for (const page of this.qualifying) {
      const entry = this.pages[page - 1];
      entry.visibleMs += delta;
      if (entry.visibleMs >= SEEN_MS) {
        if (!entry.firstSeenAt) {
          entry.firstSeenAt = now;
          newlySeen.push(page);
        }
        entry.lastSeenAt = now;
      }
    }
    return newlySeen;
  }

  // stops the clock without accruing (pause, hidden tab, overlay)
  pause(now) {
    const seen = this.tick(now);
    this.lastTick = null;
    return seen;
  }

  get allSeen() {
    return this.pages.every((p) => !!p.firstSeenAt);
  }

  snapshot() {
    return {
      pages: this.pages.map((p) => ({ ...p })),
      totalMs: this.pages.reduce((sum, p) => sum + p.visibleMs, 0),
      pageCount: this.pageCount,
      allSeen: this.allSeen,
    };
  }

  // what may be persisted per browser: first-seen times only
  toStorage() {
    const out = {};
    for (const p of this.pages) if (p.firstSeenAt) out[p.page] = p.firstSeenAt;
    return out;
  }
}
